import { memo, useCallback, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import styles from './LanguageSwitcher.module.css'

interface Language {
  code: string
  name: string
  flag: string
}

const languages: Language[] = [
  { code: 'en-US', name: 'English', flag: '🇺🇸' },
  { code: 'pt-BR', name: 'Português', flag: '🇧🇷' },
  { code: 'es-ES', name: 'Español', flag: '🇪🇸' },
]

const LanguageSwitcher = memo(() => {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  const toggleMenu = useCallback(() => setIsOpen(prev => !prev), [])

  const handleLanguageChange = useCallback((langCode: string) => {
    // Remove locale prefix from current path to get base path
    let basePath = location.pathname
    if (basePath.startsWith('/en')) basePath = basePath.slice(3) || '/'
    else if (basePath.startsWith('/es')) basePath = basePath.slice(3) || '/'
    if (!basePath) basePath = '/'

    // Navigate to the locale URL
    let target: string
    if (langCode === 'en-US') target = basePath === '/' ? '/en' : `/en${basePath}`
    else if (langCode === 'es-ES') target = basePath === '/' ? '/es' : `/es${basePath}`
    else target = basePath  // pt-BR: no prefix

    navigate(target)
    setIsOpen(false)
  }, [navigate, location.pathname])

  const isActiveLanguage = (langCode: string) => {
    const current = i18n.language ?? ''
    return current === langCode || current.startsWith(langCode.split('-')[0])
  }

  return (
    <div className={styles.switcher} ref={containerRef}>
      <div className={styles.dropdown}>
        <button
          className={styles.trigger}
          title="Change language"
          onClick={toggleMenu}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label="Selecionar idioma"
        >
          🌐
        </button>
        <div
          className={`${styles.menu} ${isOpen ? styles.open : ''}`}
          role="listbox"
          aria-label="Idiomas disponíveis"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              role="option"
              aria-selected={isActiveLanguage(lang.code)}
              onClick={() => handleLanguageChange(lang.code)}
              className={`${styles.option} ${isActiveLanguage(lang.code) ? styles.active : ''}`}
              title={lang.name}
            >
              <span className={styles.flag}>{lang.flag}</span>
              <span className={styles.name}>{lang.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
})

LanguageSwitcher.displayName = 'LanguageSwitcher'
export default LanguageSwitcher
